import { createUseStyles } from 'react-jss';
import { CmsWrenchTheme } from '~app/styles/cmsTheme';
import { getSrc } from '~app/helpers/assetLoading';
import rebrandFocusImgV1 from '~static/images/dareToAsk/rebrandEp-default.webp';
import rebrandV2BrDesktopImg from '~static/images/brazilExp/br-desktop.webp';
import rebrandIntlV2Desktop from '~static/images/rebrandIntl/rebrandIntl-desktop.webp';
import rebrandIntlV2Mobile from '~static/images/rebrandIntl/rebrandIntl-mobile.webp';
import rebrandV2BrMobileImg from '~static/images/brazilExp/br-mobile.webp';
import rebrandEpV2DesktopImg from '~static/images/rebrandEpV2/rebrandEngV2-desk.webp';
import rebrandEpV2MobileImg from '~static/images/rebrandEpV2/rebrandEngV2-mb.webp';

const imageUrls = {
  rebrandFocusV1: getSrc(rebrandFocusImgV1),
  rebrandV2Mobile: getSrc(rebrandV2BrMobileImg),
  rebrandV2Desktop: getSrc(rebrandV2BrDesktopImg),
  rebrandIntlMobile: getSrc(rebrandIntlV2Mobile),
  rebrandIntlDesktop: getSrc(rebrandIntlV2Desktop),
  rebrandEpV2Desktop: getSrc(rebrandEpV2DesktopImg),
  rebrandEpV2Mobile: getSrc(rebrandEpV2MobileImg),
} as const;

const useStyle = createUseStyles((theme: CmsWrenchTheme) => {
  const { breakpoints, brandColors, type } = theme;

  const mediaQueries = Object.fromEntries(
    Object.entries(breakpoints).map(([key, value]) => [key, `@media (min-width: ${value}px)`])
  ) as Record<keyof typeof breakpoints, string>;

  const utilities = {
    flexCenter: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    backgroundImageBase: {
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'contain',
      backgroundPosition: 'center',
    },
    textCenter: { textAlign: 'center' },
    textLeft: { textAlign: 'left' },
    fullWidth: { width: '100%' },
  };

  const layouts = {
    container: {
      padding: '20px',
      maxWidth: '1400px',
      margin: '0 auto',
      overflowX: 'hidden',
      alignItems: 'center',
      [mediaQueries.sm]: { padding: '40px 60px 100px' },
      [mediaQueries.lg]: { padding: '80px 0 100px 100px', alignItems: 'flex-start' },
      [mediaQueries.xl]: { padding: '80px 0 100px 165px' },
    },
    imageV2Dimensions: {
      base: { width: '285px', height: '275px' },
      md: { width: '540px', height: '470px' },
      lg: { width: '595px', height: '504px' },
      intlMd: { width: '400px', height: '350px' },
      intlLg: { width: '450px', height: '385px' },
    },
  };

  // Text alignment helper
  const createTextStyle = (maxWidth: string, lgMaxWidth?: string): object => ({
    fontWeight: type.fontWeight.regular,
    ...utilities.fullWidth,
    maxWidth: '1020px',
    '&, & div': utilities.textCenter,
    [mediaQueries.lg]: {
      maxWidth: lgMaxWidth ?? maxWidth,
      '&, & div': utilities.textLeft,
    },
  });

  // Generic responsive image creator for V2 images
  const createResponsiveImage = (
    mobileImage: string,
    desktopImage: string,
    config: {
      baseDimensions?: { width?: string; height?: string };
      mdDimensions?: { width?: string; height?: string };
      lgDimensions?: { width?: string; height?: string };
      marginLeft?: { md?: string; lg?: string };
    } = {}
  ): object => {
    const {
      baseDimensions = layouts.imageV2Dimensions.base,
      mdDimensions = layouts.imageV2Dimensions.md,
      lgDimensions = layouts.imageV2Dimensions.lg,
      marginLeft = { md: '160px', lg: '200px' },
    } = config;

    return {
      ...utilities.backgroundImageBase,
      backgroundSize: 'cover',
      ...baseDimensions,
      padding: '0 20px',
      backgroundImage: `url(${mobileImage})`,
      [mediaQueries.md]: {
        ...mdDimensions,
        marginLeft: marginLeft.md,
        backgroundImage: `url(${desktopImage})`,
      },
      [mediaQueries.lg]: {
        ...lgDimensions,
        marginLeft: marginLeft.lg,
      },
    };
  };

  return {
    display: { backgroundColor: brandColors.primary.vine },

    // V1 Styles
    wrapperV1: {
      ...layouts.container,
      [mediaQueries.sm]: { padding: '40px 60px 220px' },
    },

    imageV1: {
      ...utilities.backgroundImageBase,
      backgroundImage: `url(${imageUrls.rebrandFocusV1})`,
      position: 'relative',
      top: '200px',
      [mediaQueries.md]: { width: '550px', height: '433px', left: '150px' },
      [mediaQueries.lg]: { width: '620px', height: '504px', left: '112px' },
    },

    ctaFormV1: {
      margin: '0 auto',
      maxWidth: 547,
      borderRadius: [[8, 8, 0, 0]],
      padding: '24px',
      backgroundColor: '#fff',
      [mediaQueries.sm]: { borderRadius: 8, padding: [[48, 64]] },
    },

    // V2 Styles
    wrapper: layouts.container,

    imagePlacement: {
      ...utilities.backgroundImageBase,
      position: 'relative',
      top: '70px',
      [mediaQueries.md]: { width: '550px', height: '433px', left: '150px' },
      [mediaQueries.lg]: { width: '550px', height: '460px', left: '150px' },
    },

    image: {
      backgroundImage: `url(${imageUrls.rebrandFocusV1})`,
    },

    heroForm: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '20px',
      [mediaQueries.md]: { flexDirection: 'row', justifyContent: 'space-between' },
      [mediaQueries.lg]: { alignItems: 'flex-start' },
    },

    imagePlacementBr: {
      ...utilities.flexCenter,
      flexDirection: 'column',
      gap: '20px',
      [mediaQueries.md]: { justifyContent: 'space-between' },
      [mediaQueries.lg]: { flexDirection: 'row', gap: '0' },
    },

    // Optimized V2 image styles using helper function
    imageV2Brazil: createResponsiveImage(imageUrls.rebrandV2Mobile, imageUrls.rebrandV2Desktop),

    imageV2Intl: createResponsiveImage(imageUrls.rebrandIntlMobile, imageUrls.rebrandIntlDesktop, {
      baseDimensions: { ...layouts.imageV2Dimensions.base, height: '220px' },
      mdDimensions: layouts.imageV2Dimensions.intlMd,
      lgDimensions: layouts.imageV2Dimensions.intlLg,
    }),

    rebrandEpV2Img: createResponsiveImage(imageUrls.rebrandEpV2Mobile, imageUrls.rebrandEpV2Desktop, {
      baseDimensions: { ...layouts.imageV2Dimensions.base, height: '240px', width: '265px' },
      mdDimensions: layouts.imageV2Dimensions.intlMd,
      lgDimensions: layouts.imageV2Dimensions.intlLg,
    }),

    ctaForm: {
      margin: '0 auto',
      maxWidth: '380px',
    },

    header: {
      '& > section': {
        ...utilities.textCenter,
        ...utilities.fullWidth,
        maxWidth: '1020px',
        [mediaQueries.md]: { fontSize: '34px' },
        [mediaQueries.lg]: { ...utilities.textLeft, fontSize: '50px' },
      },
      [mediaQueries.lg]: { ...utilities.fullWidth },
    },

    // Using the helper function for consistent text styles
    subHeader: createTextStyle('1020px', '560px'),
    intlSubheader: createTextStyle('1020px', '460px'),
  };
});

export default useStyle;
